import classNames from 'classnames'
import { ForwardedRef, forwardRef, KeyboardEvent } from 'react'

import { useFocused } from './helpers/useFocused'
import { InputWrap } from './InputWrapper'
import { IInputCommonProps } from './models/IInputCommonProps'
import { InputField } from './resources/InputField'

import styles from './Input.module.scss'

type Ref = HTMLInputElement

export interface IInputProps extends IInputCommonProps {
  /**
   * Ref for input field
   */
  inputRef?: ForwardedRef<HTMLInputElement>
  /**
   * Callback function fires when key is pressed
   */
  onKeyPress?: (e: KeyboardEvent<HTMLInputElement>) => void
  /**
   * Callback function fires when key is pressed
   */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  /**
   * Type of input field
   */
  type?: 'text' | 'password' | 'email' | 'number' | 'search' | 'tel' | 'url'
  /**
   * Turn off browser number action buttons
   */
  noNumberSpinner?: boolean
  /**
   * Root test ID. This property is used for automated tests.
   */
  __testId?: string
}

export const Input = forwardRef<Ref, IInputProps>((props, ref) => {
  const {
    classes,
    margin,
    label,
    value,
    wrapper,
    disabled,
    startContent,
    endContent,
    dir,
    type,
    noNumberSpinner,
    placeholder,
    placeholderTemplate,
  } = props

  const { focused, setFocused } = useFocused({ disabled })
  return (
    <InputWrap
      classes={classes}
      margin={margin}
      label={label}
      value={value}
      wrapper={wrapper}
      disabled={disabled}
      startContent={startContent}
      endContent={endContent}
      dir={dir}
      focused={focused}
      onChange={props.onChange}
      placeholderTemplate={placeholderTemplate}
    >
      <InputField
        {...props}
        inputRef={props.inputRef || ref}
        focused={focused}
        setFocused={setFocused}
        placeholder={!placeholderTemplate ? placeholder : undefined}
        className={classNames(
          styles.control,
          {
            [styles['control--focused']]: label && (focused || value),
            [styles['control--noSpin']]: type === 'number' && noNumberSpinner,
          },
          classes?.control
        )}
      />
    </InputWrap>
  )
})

Input.displayName = 'Input'
